import { useEffect, useState, useCallback } from 'react';
import { useAuthStore } from '@/store/authStore';
import { useCurrentShift, useShiftControls } from '@/lib/shift/shift-controller';
import { useShiftStore } from '@/lib/shift/shift-store';
import { OnBreakDialog } from '../OnBreakDialog';
import { useNavigate } from 'react-router-dom';
import { ShiftActivityTracking } from '@/lib/shift/shift-activity-tracking';
import { WellnessCheck } from '@/components/wellness/WellnessCheck';

interface RequireShiftProps {
  children: React.ReactNode;
}

export const RequireShift = ({ children }: RequireShiftProps) => {
  const { loggingOut } = useAuthStore();
  const shift = useCurrentShift();
  const controls = useShiftControls();
  const isOnBreak = useShiftStore((s) => s.breakStartedAt != null);
  const [hasInit, setHasInit] = useState(shift.shift != null);
  const hasShift = shift.shift != null;
  const isPending = shift.isLoading || loggingOut;
  const isShiftWorthy = shift.requiresShift && !isOnBreak;
  const navigate = useNavigate();

  // Memoize the ack function to prevent unnecessary re-renders
  const handleAck = useCallback(() => {
    console.debug(`[${RequireShift.name}] Starting shift`);
    controls.ack();
  }, [controls]);

  // Memoize the navigate function to prevent unnecessary re-renders
  const navigateToDashboard = useCallback(() => {
    console.debug(`[${RequireShift.name}] Redirecting to dashboard`);
    navigate('/dashboard');
  }, [navigate]);

  useEffect(() => {
    if (isPending) return;
    if (!isShiftWorthy) return;

    if (!shift.canStartShift) {
      // User cannot start shift so redirect them
      navigateToDashboard();
      return;
    }

    if (!hasInit && !hasShift) {
      // Start shift if necessary
      handleAck();
      return;
    }

    if (hasInit && !hasShift) {
      // Has already init'd a shift before so we will redirect this time
      navigateToDashboard();
      return;
    }
  }, [
    handleAck,
    navigateToDashboard,
    hasShift,
    isShiftWorthy,
    hasInit,
    isPending,
    shift.canStartShift,
  ]);

  useEffect(() => {
    if (hasShift) {
      setHasInit(true);
    }
  }, [hasShift]);

  if (loggingOut) {
    return null;
  }

  if (shift.shift == null && shift.requiresShift) {
    return <>{isOnBreak && <OnBreakDialog />}</>;
  }

  return (
    <>
      <WellnessCheck />
      <ShiftActivityTracking />
      {(shift.status !== 'idle' || !isShiftWorthy) && children}
    </>
  );
};
